import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import SEO from "@/components/SEO";
import NewsletterSignup from "@/components/NewsletterSignup";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function ChroniclePost() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [categoryLabel, setCategoryLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState([]);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/chronicle/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => {
        nav("/chronicle", { replace: true });
      })
      .finally(() => setLoading(false));
  }, [slug, nav]);

  useEffect(() => {
    api.get("/chronicle/categories").then((r) => {
      const map = Object.fromEntries((r.data || []).map((c) => [c.key, c.label]));
      if (post) setCategoryLabel(map[post.category] || post.category);
    });
  }, [post]);

  useEffect(() => {
    if (!post) return;
    api
      .get("/chronicle", { params: { limit: 6 } })
      .then((r) => setMore((r.data || []).filter((p) => p.id !== post.id).slice(0, 3)));
  }, [post]);

  if (loading) {
    return (
      <div className="mx-auto max-w-[860px] px-6 py-32 md:px-10">
        <div className="h-12 w-2/3 animate-pulse bg-[#EBE7DD]" />
        <div className="mt-6 aspect-[16/10] w-full animate-pulse bg-[#EBE7DD]" />
      </div>
    );
  }

  if (!post) return null;

  return (
    <article data-testid="chronicle-post" className="bg-[#F9F9F4] text-[#1A3626]">
      <SEO
        title={post.title}
        path={`/chronicle/${post.slug}`}
        description={post.excerpt || post.title}
        image={post.hero_image_url ? fileUrl(post.hero_image_url) : undefined}
      />

      {/* Top breadcrumb back-to-Chronicle */}
      <div className="bg-[#F9F9F4]">
        <div className="mx-auto max-w-[1200px] px-6 pt-12 md:px-10 md:pt-20">
          <Link
            to="/chronicle"
            className="font-display inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.36em] text-[#1A3626]/70 transition-colors hover:text-[#1A3626] md:text-xs"
            data-testid="chronicle-back"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2.6} />
            The Cabbage Chronicle
          </Link>
        </div>
      </div>

      {/* MASTHEAD */}
      <header className="bg-[#F9F9F4]">
        <div className="mx-auto max-w-[860px] px-6 pb-10 pt-10 md:px-10 md:pb-14 md:pt-16">
          <div
            className="font-display text-[10px] font-black uppercase tracking-[0.42em] text-[#93C065] md:text-xs"
            data-testid="chronicle-post-category"
          >
            {categoryLabel || post.category}
          </div>
          <h1
            className="font-display mt-5 text-4xl font-black uppercase leading-[0.92] tracking-tight sm:text-5xl md:text-6xl lg:text-7xl"
            data-testid="chronicle-post-title"
          >
            {post.title}
          </h1>
          {post.excerpt && (
            <p
              className="mt-7 max-w-[640px] text-lg leading-relaxed text-[#1A3626]/90 md:text-2xl md:leading-[1.55]"
              data-testid="chronicle-post-excerpt"
            >
              {post.excerpt}
            </p>
          )}
          <div className="mt-8 flex flex-wrap items-center gap-3 text-xs text-[#1A3626]/60 md:text-sm">
            <span data-testid="chronicle-post-author">{post.author}</span>
            <span aria-hidden>·</span>
            <span data-testid="chronicle-post-date">{formatDate(post.published_at)}</span>
          </div>
        </div>
      </header>

      {/* HERO IMAGE */}
      {post.hero_image_url && (
        <div className="bg-[#F9F9F4]">
          <div className="mx-auto max-w-[1200px] px-6 md:px-10">
            <div className="aspect-[16/10] overflow-hidden border-2 border-[#1A3626] bg-[#EBE7DD]">
              <img
                src={fileUrl(post.hero_image_url)}
                alt={post.hero_image_alt || post.title}
                className="h-full w-full object-cover"
                data-testid="chronicle-post-hero"
              />
            </div>
          </div>
        </div>
      )}

      {/* BODY */}
      <section className="bg-[#F9F9F4]">
        <div className="mx-auto max-w-[760px] px-6 pb-12 pt-12 md:px-10 md:pb-16 md:pt-20">
          <div
            className="chronicle-body"
            dangerouslySetInnerHTML={{ __html: post.body_html || "" }}
            data-testid="chronicle-post-body"
          />

          {post.pull_quote && (
            <blockquote
              className="my-12 border-l-4 border-[#93C065] pl-6 md:my-16 md:pl-10"
              data-testid="chronicle-post-pullquote"
            >
              <p className="font-display text-2xl font-black uppercase leading-[1.05] tracking-tight text-[#1A3626] md:text-4xl">
                &ldquo;{post.pull_quote}&rdquo;
              </p>
            </blockquote>
          )}
        </div>
      </section>

      {/* NEWSLETTER — captures readers at the end of a post */}
      <NewsletterSignup variant="block" source="chronicle-end" />

      {/* MORE FROM THE CHRONICLE */}
      {more.length > 0 && (
        <section className="border-t border-[#1A3626]/15 bg-[#F9F9F4] py-16 md:py-24" data-testid="chronicle-more">
          <div className="mx-auto max-w-[1200px] px-6 md:px-10">
            <div className="font-display text-[10px] font-black uppercase tracking-[0.42em] text-[#93C065] md:text-xs">
              Further reading
            </div>
            <h2 className="font-display mt-4 text-3xl font-black uppercase leading-[0.95] tracking-tight sm:text-4xl md:text-5xl">
              More from the Chronicle.
            </h2>
            <div className="mt-10 grid grid-cols-1 gap-x-10 gap-y-12 md:grid-cols-3 md:gap-x-12">
              {more.map((p) => (
                <Link
                  to={`/chronicle/${p.slug}`}
                  key={p.id}
                  className="group block"
                  data-testid={`chronicle-more-${p.slug}`}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-[#EBE7DD]">
                    {p.hero_image_url ? (
                      <img
                        src={fileUrl(p.hero_image_url)}
                        alt={p.hero_image_alt || p.title}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center font-display text-xs uppercase tracking-[0.3em] text-[#1A3626]/40">
                        {p.category}
                      </div>
                    )}
                  </div>
                  <h3 className="font-display mt-4 text-lg font-black uppercase leading-[1.1] tracking-tight md:text-xl">
                    {p.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Styles for body HTML (mirrors the Admin editor styling) */}
      <style>{`
        .chronicle-body p { font-size: 18px; line-height: 1.78; margin: 0 0 1.2em; color: #1A3626; }
        .chronicle-body h2 { font-family: 'Bodoni Moda', serif; font-size: 36px; line-height: 1.05; font-weight: 900; letter-spacing: -0.01em; margin: 1.6em 0 0.6em; text-transform: uppercase; color: #1A3626; }
        .chronicle-body h3 { font-family: 'Bodoni Moda', serif; font-size: 24px; line-height: 1.15; font-weight: 900; letter-spacing: -0.005em; margin: 1.4em 0 0.5em; text-transform: uppercase; color: #1A3626; }
        @media (min-width: 768px) {
          .chronicle-body p { font-size: 19px; }
          .chronicle-body h2 { font-size: 48px; }
          .chronicle-body h3 { font-size: 30px; }
        }
        .chronicle-body ul, .chronicle-body ol { padding-left: 1.5em; margin: 0 0 1.2em; }
        .chronicle-body li { font-size: 18px; line-height: 1.72; margin-bottom: 0.35em; color: #1A3626; }
        .chronicle-body blockquote {
          border-left: 3px solid #93C065; margin: 1.6em 0; padding: 0.4em 0 0.4em 1.1em; font-style: italic; color: #1A3626;
        }
        .chronicle-body a { color: #1A3626; text-decoration: underline; text-decoration-color: #93C065; text-decoration-thickness: 2px; text-underline-offset: 4px; }
        .chronicle-body img { max-width: 100%; height: auto; display: block; margin: 2em auto; border: 2px solid #1A3626; }
        .chronicle-body hr { border: 0; border-top: 2px solid #1A3626; margin: 2.5em 0; }
        .chronicle-body strong { font-weight: 800; }
      `}</style>
    </article>
  );
}
